import i18n from 'helpers/i18n';
import { useEffect, useRef, useState } from 'react';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/PageHeader';
import MainContent from 'components/MainContent';
import { useMediaQuery } from 'react-responsive';
import useCurrentUser from 'hooks/useCurrentUser';
import PageBackground from 'components/PageBackground';
import exitTest from 'images/reliabilityTest/exitTest.svg';
import { SecondaryActionWrapper } from '../Styled';
import { Button, Text } from '@teachstonellc/component-library';
import instructionsIcon from 'images/reliabilityTest/instructionsIcon.svg';
import logoCertification from 'images/reliabilityTest/logoCertification.svg';
import {
  StyledButton,
  ActionWrapper,
  SectionActions,
  ContainerSection as Section
} from 'components/Reports/Reliability/View/Styled';
import HonorCodeAgreementContent from './HonorCodeAgreementContent';
import { updateUserTestTermsAccepted } from 'actions/userTests';
import { updateUserCalibrationTermsAccepted } from 'actions/testPreparations';
import InstructionsModal from 'components/Modals/InstructionsModal';

interface HonorCodeAgreementProps {
  title: string;
  testType?: string;
  userTestId: string;
  testAttemptId: number;
  handlePreTestState: (value: string) => void;
}

function HonorCodeAgreement({
  title,
  testType,
  userTestId,
  testAttemptId,
  handlePreTestState
}: HonorCodeAgreementProps) {
  const [honorCodeAccepted, setHonorCodeAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [buttonMsg, setButtonMsg] = useState(messages.startTest);
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const isTablet = useMediaQuery({ minWidth: 768 });
  const honorCodeRef = useRef<any>({ current: null });

  useEffect(() => {
    const honorCodeElement = document.getElementById('honor-code');

    if (honorCodeElement) {
      honorCodeRef.current = honorCodeElement;
      honorCodeRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (testType === 'calibration') {
      setButtonMsg(messages.startCalibration);
    }
  }, [testType]);

  function setPreTestState() {
    switch (testType) {
      case 'knowledge':
        handlePreTestState('knowledge_test');
        break;
      case 'reliability':
        handlePreTestState('reliability_test');
        break;
      case 'calibration':
        handlePreTestState('calibration_test');
        break;
      default:
        handlePreTestState('instructions');
    }
    window.localStorage.removeItem(`pre_test_state_${testAttemptId}`);
  }

  function acceptHonorCodeAgreement() {
    if (honorCodeAccepted) {
      let termsAcceptedAt = new Date();
      if (testType === 'calibration') {
        updateUserCalibrationTermsAccepted(parseInt(userTestId)).then(() => {
          setHonorCodeAccepted(true);
          setPreTestState();
        });
      } else {
        updateUserTestTermsAccepted(
          currentUser.id,
          parseInt(userTestId),
          termsAcceptedAt.toISOString()
        ).then(() => setPreTestState());
      }
    } else {
      setShowModal(true);
    }
  }

  function handleClick() {
    return navigate(RouteHelpers.getPath('app'));
  }

  function back() {
    handlePreTestState('instructions');
  }

  return (
    <MainContent maxWidth={1080}>
      <InstructionsModal
        title={i18n.ft(messages.honorCodeAgreement)}
        message={<p className="text-center">{i18n.ft(messages.warning)}</p>}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        confirmButton={i18n.ft(messages.dismiss)}
      />

      {testType !== 'calibration' && (
        <PageHeader
          title={title}
          subtitle={
            testType === 'knowledge'
              ? i18n.ft(messages.review)
              : i18n.ft(messages.subtitle)
          }
          image={logoCertification}
          imageAlt={i18n.ft(messages.logoDescription)}
        />
      )}
      <PageBackground borderStyle>
        <Section
          mt={{ _: '0px', lg: '32px' }}
          mx={{ _: '0px', lg: '32px' }}
          borderTopRadius={isTablet ? '12px' : '0px'}
        >
          <div className="bg-white p-6 rounded-t-xl md:py-10  md:px-16 md:mb-1 lg:mb-1">
            <div className="flex mb-5">
              <img className="w-5 md:w-9" src={instructionsIcon} alt="" />
              <Text
                as="h2"
                id="honor-code"
                className="pl-3 self-center"
                tabIndex={-1}
              >
                {i18n.ft(messages.honorCodeAgreement)}
              </Text>
            </div>
            <HonorCodeAgreementContent
              testType={testType}
              setHonorCode={setHonorCodeAccepted}
            />
          </div>
        </Section>
        <Section
          mb={{ _: '0px', lg: '32px' }}
          mx={{ _: '0px', lg: '32px' }}
          borderBottomRadius={isTablet ? '12px' : '0px'}
        >
          <div className="bg-white p-6 rounded-b-xl lg:p-6 lg:m-1">
            <SectionActions>
              <ActionWrapper>
                <StyledButton
                  basic
                  fluid
                  content={
                    <div className="cursor-pointer flex items-center">
                      <img src={exitTest} alt="" className="mr-5" />
                      <span className="m-auto">
                        {i18n.ft(messages.exitTest)}
                      </span>
                    </div>
                  }
                  onClick={handleClick}
                />
              </ActionWrapper>
              <SecondaryActionWrapper>
                <Button fullWidth variant="secondary" onClick={back}>
                  {i18n.ft(messages.back)}
                </Button>
              </SecondaryActionWrapper>
              <ActionWrapper>
                <Button
                  fullWidth
                  variant="primary"
                  className="whitespace-nowrap"
                  onClick={() => acceptHonorCodeAgreement()}
                >
                  {testType === 'knowledge'
                    ? i18n.ft(messages.next)
                    : i18n.ft(buttonMsg)}
                </Button>
              </ActionWrapper>
            </SectionActions>
          </div>
        </Section>
      </PageBackground>
    </MainContent>
  );
}

export default HonorCodeAgreement;
